import React, { useState, useMemo } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Typography,
  IconButton,
  Skeleton,
  TextField,
  InputAdornment,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  ListItemIcon,
} from '@mui/material';
import Fade from '@mui/material/Fade';
import KeyboardArrowDownRounded from '@mui/icons-material/KeyboardArrowDownRounded';
import CheckRounded from '@mui/icons-material/CheckRounded';
import {
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  UnfoldMore as UnfoldMoreIcon,
} from '@mui/icons-material';

import EditIcon from '../../Images/Edit.png';
import DeleteIcon from '../../Images/Delete.png';
import PropTypes from 'prop-types';
import NewWorkflowModal from './NewWorkflowModal';
import { motion, AnimatePresence } from 'framer-motion';
import SearchIcon from '@mui/icons-material/Search';
import CloseRounded from '@mui/icons-material/CloseRounded';
import VerifyCaptchaDialog from '../connectionPage/VerifyCaptchaDialog';
import StarFilled from '../../Images/FilledStar.png';
import StarOutline from '../../Images/Outlinedstar.png';
import { patchToggleWorkflowStar } from '../../apis/Workflows';
import { useDispatch } from 'react-redux';
import { fetchWorkflows } from '../../state/slices/workflowSlice';
import { toast } from 'react-toastify';
import { useTimezone } from '../../hooks/useTimezone';
import Cookies from 'js-cookie';

const WorkflowTable = ({
  title,
  workflows,
  loading,
  onEdit,
  onDelete,
  showCreateButton = false,
  selectedProject = null,
}) => {
  const [showAll, setShowAll] = React.useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [captchaOpen, setCaptchaOpen] = useState(false);
  const [pendingWorkflow, setPendingWorkflow] = useState(null);
  const [sortConfig, setSortConfig] = useState({
    column: null,
    direction: 'none',
  });

  const [runModeFilter, setRunModeFilter] = useState('');
  const [ownerFilter, setOwnerFilter] = useState('');
  const [organizationFilter, setOrganizationFilter] = useState('');
  const dispatch = useDispatch();
  const { formatDate } = useTimezone();

  const rawRole = Cookies.get('Role_id') ?? Cookies.get('role_id');
  const isSuperAdmin = useMemo(() => {
    const n = Number(rawRole);
    return !Number.isNaN(n) && n === 1;
  }, [rawRole]);
  const isAdmin = useMemo(() => {
    const n = Number(rawRole);
    return !Number.isNaN(n) && n === 2;
  }, [rawRole]);

  const handleEditClick = (e, row) => {
    e.stopPropagation();
    setPendingWorkflow({
      id: row.workflow_id,
      name: row.workflow_name || row.name,
    });
    setCaptchaOpen(true);
  };

  const handleVerified = () => {
    if (pendingWorkflow?.id) onEdit(pendingWorkflow.id);
    setCaptchaOpen(false);
    setPendingWorkflow(null);
  };

  const handleClose = () => {
    setCaptchaOpen(false);
    setPendingWorkflow(null);
  };

  const handleSort = (column) => {
    let direction = 'asc';

    if (sortConfig.column === column && sortConfig.direction === 'asc') {
      direction = 'desc';
    } else if (
      sortConfig.column === column &&
      sortConfig.direction === 'desc'
    ) {
      direction = 'none';
      column = null;
    }

    setSortConfig({ column, direction });
  };

  const getSortIcon = (column) => {
    // Only show active sort icon for the currently sorted column
    if (sortConfig.column === column && sortConfig.direction !== 'none') {
      return sortConfig.direction === 'asc' ? (
        <ArrowUpwardIcon sx={{ fontSize: 16, ml: 0.5 }} />
      ) : (
        <ArrowDownwardIcon sx={{ fontSize: 16, ml: 0.5 }} />
      );
    }

    // Show subtle icon for all other columns
    return <UnfoldMoreIcon sx={{ fontSize: 16, opacity: 0.5, ml: 0.5 }} />;
  };

  const filterSelectSx = {
    fontSize: '0.875rem',
    borderRadius: '10px',
    bgcolor: '#fff',
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: '#e2e8f0',
      transition: 'border-color .2s ease, box-shadow .2s ease',
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: '#5965A6',
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: '#8E98CD',
      boxShadow: '0 0 0 2px rgba(89,101,166,0.14)',
    },
    '& .MuiSelect-select': {
      py: 0.75,
      px: 1.25,
      fontSize: '0.75rem',
    },
    '& .MuiSelect-icon': {
      color: '#94a3b8',
      transition: 'transform .2s ease',
    },
    '&.Mui-expanded .MuiSelect-icon': {
      transform: 'rotate(180deg)',
    },
    transition:
      'box-shadow .2s ease, background-color .2s ease, border-color .2s ease',
  };

  const filterMenuProps = {
    TransitionComponent: Fade,
    PaperProps: {
      sx: {
        mt: 0.5,
        borderRadius: '10px',
        border: '1px solid #e5e7eb',
        boxShadow: '0 10px 24px rgba(0,0,0,0.12)',
        overflow: 'hidden',
      },
    },
    MenuListProps: { dense: true, sx: { py: 0.5 } },
  };

  const filterMenuItemSx = {
    fontSize: '0.875rem',
    px: 1.25,
    py: 0.75,
    transition: 'background-color .15s ease',
    '&:hover': { backgroundColor: '#F3F6FF' },
    '&.Mui-selected, &.Mui-selected:hover': {
      backgroundColor: '#EEF2FF',
      color: '#5965A6',
      fontWeight: 600,
    },
  };

  const filterListItemIconSx = {
    minWidth: 24,
    color: '#5965A6',
  };

  const filterOptions = useMemo(() => {
    const runModes = new Set();
    const owners = new Set();
    const organizations = new Set();

    if (Array.isArray(workflows) && workflows.length > 0) {
      workflows.forEach((row) => {
        const runMode = row?.is_scheduled ? 'Schedule' : 'On Demand';
        runModes.add(runMode);

        if (row?.user_name) {
          owners.add(row.user_name);
        }

        if (row?.org_name) {
          organizations.add(row.org_name);
        }
      });
    }

    return {
      runModes: Array.from(runModes).sort(),
      owners: Array.from(owners).sort(),
      organizations: Array.from(organizations).sort(),
    };
  }, [workflows]);
  const containerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.4,
        type: 'spring',
        stiffness: 100,
      },
    },
  };

  const rowVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: (index) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: index * 0.05,
        duration: 0.3,
        ease: 'easeOut',
      },
    }),
    hover: {
      scale: 1.01,
      backgroundColor: 'rgba(79, 172, 254, 0.05)',
      transition: {
        duration: 0.2,
        ease: 'easeInOut',
      },
    },
  };

  // Apply sorting and filtering
  const filteredRows = useMemo(() => {
    const workflowsArray = Array.isArray(workflows) ? workflows : [];
    let filtered = [...workflowsArray];

    // Apply search filter
    const q = search.trim().toLowerCase();
    if (q) {
      filtered = filtered.filter((r) => {
        const name = (r?.name ?? r?.workflow_name ?? '')
          .toString()
          .toLowerCase();
        const desc = (r?.description ?? '').toString().toLowerCase();
        const userName = (r?.user_name || '').toString().toLowerCase();
        const orgName = (r?.org_name || '').toString().toLowerCase();
        return (
          name.includes(q) ||
          desc.includes(q) ||
          userName.includes(q) ||
          orgName.includes(q)
        );
      });
    }

    if (runModeFilter) {
      filtered = filtered.filter((r) => {
        const runMode = r.is_scheduled ? 'Schedule' : 'On Demand';
        return runMode === runModeFilter;
      });
    }

    if (ownerFilter) {
      filtered = filtered.filter((r) => r?.user_name === ownerFilter);
    }

    if (organizationFilter) {
      filtered = filtered.filter((r) => r?.org_name === organizationFilter);
    }

    // Apply sorting
    if (sortConfig.direction !== 'none' && sortConfig.column) {
      filtered = [...filtered].sort((a, b) => {
        let aValue, bValue;

        switch (sortConfig.column) {
          case 'workflow_name':
            aValue = (a.name || '').toLowerCase();
            bValue = (b.name || '').toLowerCase();
            break;
          case 'created_at':
            aValue = a.created_at ? new Date(a.created_at).getTime() : 0;
            bValue = b.created_at ? new Date(b.created_at).getTime() : 0;
            break;
          case 'description':
            aValue = (a.description || '').toLowerCase();
            bValue = (b.description || '').toLowerCase();
            break;
          case 'run_mode':
            aValue = (a.is_scheduled ? 'schedule' : 'on demand').toLowerCase();
            bValue = (b.is_scheduled ? 'schedule' : 'on demand').toLowerCase();
            break;
          case 'user_name':
            aValue = (a.user_name || '').toLowerCase();
            bValue = (b.user_name || '').toLowerCase();
            break;
          case 'org_name':
            aValue = (a.org_name || '').toLowerCase();
            bValue = (b.org_name || '').toLowerCase();
            break;
          default:
            return 0;
        }

        if (sortConfig.column === 'created_at') {
          // For dates, higher timestamp = more recent
          return sortConfig.direction === 'asc'
            ? aValue - bValue
            : bValue - aValue;
        } else {
          // For strings
          if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
          if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
          return 0;
        }
      });
    } else {
      // Default sorting by created_at desc if no sorting is applied
      filtered = filtered.sort(
        (a, b) =>
          (Date.parse(b?.created_at) || 0) - (Date.parse(a?.created_at) || 0),
      );
    }

    return filtered;
  }, [
    workflows,
    search,
    sortConfig,
    runModeFilter,
    ownerFilter,
    organizationFilter,
  ]);

  const displayedRows = showAll ? filteredRows : filteredRows.slice(0, 10);

  // prefer server field `row.starred` (fallbacks for legacy)
  const isRowStarred = (row) =>
    Boolean(row?.starred ?? row?.is_starred ?? false);

  // optional: block double clicks during update
  const [starUpdating, setStarUpdating] = useState(() => new Set());
  const isStarUpdating = (id) => starUpdating.has(String(id));
  const setUpdating = (id, on) =>
    setStarUpdating((prev) => {
      const s = new Set(prev);
      on ? s.add(String(id)) : s.delete(String(id));
      return s;
    });

  const onToggleStar = async (workflowId, nextStarred) => {
    const id = String(workflowId);
    try {
      setUpdating(id, true);
      await patchToggleWorkflowStar(id, nextStarred); // send {starred: boolean}
      await dispatch(fetchWorkflows()); // pull fresh server state
      toast.success(
        nextStarred ? 'Marked as Starred' : 'Removed from Starred',
        { autoClose: 1500 },
      );
    } catch (err) {
      console.error('Star toggle failed', err?.response?.data || err?.message);
      toast.error(err?.response?.data?.detail ?? 'Failed to update star');
    } finally {
      setUpdating(id, false);
    }
  };

  return (
    <motion.div variants={containerVariants} initial="hidden" animate="visible">
      <Box
        component={motion.div}
        sx={{
          borderRadius: '12px',
          background: '#ffffff',
          border: '1px solid #E5E7EB',
          p: 0,
          mt: 1,
          mb: 2,
          position: 'relative',
          overflow: 'hidden',
          boxShadow:
            '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
        }}
      >
        {/* Header */}
        <motion.div variants={itemVariants}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              p: 2,
              borderBottom: '1px solid #E5E7EB',
              backgroundColor: '#ffffff',
            }}
          >
            <Box>
              <Typography
                sx={{
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  color: '#111827',
                  mb: 0,
                }}
              >
                {title}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {/* Search toggle / bar */}
              <AnimatePresence initial={false}>
                {searchOpen ? (
                  <motion.div
                    key="searchbar"
                    initial={{ width: 0, opacity: 0 }}
                    animate={{ width: 280, opacity: 1 }}
                    exit={{ width: 0, opacity: 0 }}
                    transition={{ type: 'tween', duration: 0.25 }}
                  >
                    <TextField
                      autoFocus
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder="Search workflows…"
                      size="small"
                      fullWidth
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon
                              fontSize="small"
                              sx={{ color: '#6B7280' }}
                            />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              size="small"
                              onClick={() => {
                                setSearch('');
                                setSearchOpen(false);
                              }}
                              edge="end"
                            >
                              <CloseRounded sx={{ fontSize: 18 }} />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          height: 34,
                          borderRadius: '8px',
                          background: '#F8FAFC',
                          fontSize: '0.75rem',
                        },
                      }}
                    />
                  </motion.div>
                ) : (
                  <motion.div
                    key="searchicon"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                  >
                    <Tooltip title="Search workflows">
                      <IconButton
                        onClick={() => setSearchOpen(true)}
                        size="small"
                        sx={{
                          border: '1px solid rgba(79, 172, 254, 0.2)',
                          background: '#EEF2FF',
                          color: '#5965A6',
                          boxShadow: '0 1px 2px rgba(0,0,0,0.08)',
                          transition:
                            'transform .18s cubic-bezier(.2,.8,.2,1), box-shadow .2s ease, background-color .2s ease',
                          willChange: 'transform, box-shadow',
                          '&:hover': {
                            background: '#E5E9FF',
                            transform: 'translateY(-1px) scale(1.02)',
                            boxShadow: '0 6px 16px rgba(0,0,0,0.16)',
                          },
                          '&:active': {
                            transform: 'translateY(0) scale(.99)',
                            boxShadow: '0 3px 8px rgba(0,0,0,0.12)',
                          },
                          '&:focus-visible': {
                            outline: 'none',
                            boxShadow: '0 0 0 3px rgba(89,101,166,0.35)',
                          },
                        }}
                      >
                        <SearchIcon />
                      </IconButton>
                    </Tooltip>
                  </motion.div>
                )}
              </AnimatePresence>
              {/* Toggle Button */}
              {/* {sortedWorkflows.length > 10 && ( */}
              {filteredRows.length > 10 && (
                <motion.div
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setShowAll(!showAll)}
                >
                  <Typography
                    sx={{
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      color: '#5965A6', // keep same color as default
                      cursor: 'pointer',
                      px: 2,
                      py: 1,
                      borderRadius: '8px',
                      border: '1px solid rgba(79, 172, 254, 0.2)',
                      background: '#EEF2FF',
                      userSelect: 'none',
                      boxShadow: '0 1px 2px rgba(0,0,0,0.08)',
                      transition:
                        'box-shadow 200ms ease, outline-color 200ms ease',
                      willChange: 'box-shadow',

                      '&:hover': {
                        boxShadow: '0 6px 16px rgba(0,0,0,0.16)',
                      },
                      '&:active': {
                        boxShadow: '0 3px 8px rgba(0,0,0,0.12)',
                      },
                      '&:focus-visible': {
                        outline: 'none',
                        boxShadow: '0 0 0 3px rgba(89,101,166,0.35)',
                      },
                    }}
                  >
                    {/* {showAll ? 'View Less' : `View All (${sortedWorkflows.length})`} */}
                    {showAll
                      ? 'View Less'
                      : `View All (${filteredRows.length})`}
                  </Typography>
                </motion.div>
              )}

              {/* Add New Button */}
              {showCreateButton && selectedProject && (
                <motion.div whileTap={{ scale: 0.98 }}>
                  <NewWorkflowModal selectedProject={selectedProject} />
                </motion.div>
              )}
            </Box>
          </Box>
        </motion.div>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: 2,
            borderBottom: '1px solid #E5E7EB',
            backgroundColor: '#ffffff',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel sx={{ fontSize: '0.8rem' }}>
                Filter by Run Mode
              </InputLabel>
              <Select
                value={runModeFilter}
                onChange={(e) => setRunModeFilter(e.target.value)}
                label="Filter by Run Mode"
                IconComponent={KeyboardArrowDownRounded}
                MenuProps={filterMenuProps}
                sx={filterSelectSx}
              >
                <MenuItem value="" sx={filterMenuItemSx}>
                  <ListItemIcon
                    sx={{
                      ...filterListItemIconSx,
                      visibility: runModeFilter === '' ? 'visible' : 'hidden',
                    }}
                  >
                    <CheckRounded fontSize="small" />
                  </ListItemIcon>
                  All Modes
                </MenuItem>
                {filterOptions.runModes.map((mode) => (
                  <MenuItem key={mode} value={mode} sx={filterMenuItemSx}>
                    <ListItemIcon
                      sx={{
                        ...filterListItemIconSx,
                        visibility:
                          runModeFilter === mode ? 'visible' : 'hidden',
                      }}
                    >
                      <CheckRounded fontSize="small" />
                    </ListItemIcon>
                    {mode}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {(isAdmin || isSuperAdmin) && (
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel sx={{ fontSize: '0.8rem' }}>
                  Filter by Owner
                </InputLabel>
                <Select
                  value={ownerFilter}
                  onChange={(e) => setOwnerFilter(e.target.value)}
                  label="Filter by Owner"
                  IconComponent={KeyboardArrowDownRounded}
                  MenuProps={filterMenuProps}
                  sx={filterSelectSx}
                >
                  <MenuItem value="" sx={filterMenuItemSx}>
                    <ListItemIcon
                      sx={{
                        ...filterListItemIconSx,
                        visibility: ownerFilter === '' ? 'visible' : 'hidden',
                      }}
                    >
                      <CheckRounded fontSize="small" />
                    </ListItemIcon>
                    All Owners
                  </MenuItem>
                  {filterOptions.owners.map((owner) => (
                    <MenuItem key={owner} value={owner} sx={filterMenuItemSx}>
                      <ListItemIcon
                        sx={{
                          ...filterListItemIconSx,
                          visibility:
                            ownerFilter === owner ? 'visible' : 'hidden',
                        }}
                      >
                        <CheckRounded fontSize="small" />
                      </ListItemIcon>
                      {owner}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {isSuperAdmin && (
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel sx={{ fontSize: '0.8rem' }}>
                  Filter by Organization
                </InputLabel>
                <Select
                  value={organizationFilter}
                  onChange={(e) => setOrganizationFilter(e.target.value)}
                  label="Filter by Organization"
                  IconComponent={KeyboardArrowDownRounded}
                  MenuProps={filterMenuProps}
                  sx={filterSelectSx}
                >
                  <MenuItem value="" sx={filterMenuItemSx}>
                    <ListItemIcon
                      sx={{
                        ...filterListItemIconSx,
                        visibility:
                          organizationFilter === '' ? 'visible' : 'hidden',
                      }}
                    >
                      <CheckRounded fontSize="small" />
                    </ListItemIcon>
                    All Organizations
                  </MenuItem>
                  {filterOptions.organizations.map((org) => (
                    <MenuItem key={org} value={org} sx={filterMenuItemSx}>
                      <ListItemIcon
                        sx={{
                          ...filterListItemIconSx,
                          visibility:
                            organizationFilter === org ? 'visible' : 'hidden',
                        }}
                      >
                        <CheckRounded fontSize="small" />
                      </ListItemIcon>
                      {org}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Box>
        </Box>

        {/* Table */}
        <motion.div variants={itemVariants}>
          <TableContainer
            sx={{ backgroundColor: '#ffffff', overflow: 'hidden' }}
          >
            <Table size="small">
              <TableHead sx={{ backgroundColor: '#5965A6' }}>
                <TableRow
                  sx={{
                    '&:hover': {
                      backgroundColor: '#4C5B9A',
                    },
                    transition: 'background-color 0.2s ease',
                  }}
                >
                  <TableCell
                    sx={{
                      fontSize: '0.75rem',
                      fontWeight: 500,
                      // py: 1,
                      px: 2,
                      color: '#ffffff',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                      borderBottom: '1px solid #E5E7EB',
                      width: '80px',
                    }}
                  >
                    Sr No
                  </TableCell>
                  <TableCell
                    onClick={() => handleSort('workflow_name')}
                    sx={{
                      fontSize: '0.75rem',
                      fontWeight: 500,
                      color: '#ffffff',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                      borderBottom: '1px solid #E5E7EB',
                      cursor: 'pointer',
                      userSelect: 'none',
                      '&:hover': {
                        backgroundColor: '#4C5B9A',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                      }}
                    >
                      <span>Workflow Name</span>
                      {getSortIcon('workflow_name')}
                    </Box>
                  </TableCell>
                  <TableCell
                    onClick={() => handleSort('created_at')}
                    sx={{
                      fontSize: '0.75rem',
                      fontWeight: 500,
                      color: '#ffffff',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                      borderBottom: '1px solid #E5E7EB',
                      cursor: 'pointer',
                      userSelect: 'none',
                      '&:hover': {
                        backgroundColor: '#4C5B9A',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                      }}
                    >
                      <span>Date & Time</span>
                      {getSortIcon('created_at')}
                    </Box>
                  </TableCell>
                  <TableCell
                    onClick={() => handleSort('description')}
                    sx={{
                      fontSize: '0.75rem',
                      fontWeight: 500,
                      color: '#ffffff',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                      borderBottom: '1px solid #E5E7EB',
                      cursor: 'pointer',
                      userSelect: 'none',
                      '&:hover': {
                        backgroundColor: '#4C5B9A',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                      }}
                    >
                      <span>Description</span>
                      {getSortIcon('description')}
                    </Box>
                  </TableCell>
                  <TableCell
                    onClick={() => handleSort('run_mode')}
                    sx={{
                      fontSize: '0.75rem',
                      fontWeight: 500,
                      color: '#ffffff',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                      borderBottom: '1px solid #E5E7EB',
                      cursor: 'pointer',
                      userSelect: 'none',
                      '&:hover': {
                        backgroundColor: '#4C5B9A',
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                      }}
                    >
                      <span>Run Mode</span>
                      {getSortIcon('run_mode')}
                    </Box>
                  </TableCell>
                  {(isAdmin || isSuperAdmin) && (
                    <TableCell
                      onClick={() => handleSort('user_name')}
                      sx={{
                        fontSize: '0.75rem',
                        fontWeight: 500,
                        color: '#ffffff',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        borderBottom: '1px solid #E5E7EB',
                        cursor: 'pointer',
                        userSelect: 'none',
                        '&:hover': {
                          backgroundColor: '#4C5B9A',
                        },
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                        }}
                      >
                        <span>Owner</span>
                        {getSortIcon('user_name')}
                      </Box>
                    </TableCell>
                  )}
                  {isSuperAdmin && (
                    <TableCell
                      onClick={() => handleSort('org_name')}
                      sx={{
                        fontSize: '0.75rem',
                        fontWeight: 500,
                        color: '#ffffff',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        borderBottom: '1px solid #E5E7EB',
                        cursor: 'pointer',
                        userSelect: 'none',
                        '&:hover': {
                          backgroundColor: '#4C5B9A',
                        },
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                        }}
                      >
                        <span>Organization</span>
                        {getSortIcon('org_name')}
                      </Box>
                    </TableCell>
                  )}
                  <TableCell
                    sx={{
                      fontSize: '0.75rem',
                      fontWeight: 500,
                      color: '#ffffff',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                      borderBottom: '1px solid #E5E7EB',
                    }}
                  >
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  [...Array(5)].map((_, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        <Skeleton
                          variant="rectangular"
                          width={20}
                          height={20}
                        />
                      </TableCell>
                      <TableCell>
                        <Skeleton variant="text" width={80} />
                      </TableCell>
                      <TableCell>
                        <Skeleton variant="text" width={120} />
                      </TableCell>
                      <TableCell>
                        <Skeleton variant="text" width={200} />
                      </TableCell>
                      <TableCell>
                        <Skeleton variant="text" width={100} />
                      </TableCell>
                      {(isAdmin || isSuperAdmin) && (
                        <TableCell>
                          <Skeleton variant="text" width={80} />
                        </TableCell>
                      )}
                      {isSuperAdmin && (
                        <TableCell>
                          <Skeleton variant="text" width={100} />
                        </TableCell>
                      )}
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Skeleton variant="circular" width={32} height={32} />
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                ) : displayedRows.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={
                        6 +
                        (isAdmin || isSuperAdmin ? 1 : 0) +
                        (isSuperAdmin ? 1 : 0)
                      }
                      align="center"
                      sx={{ py: 4 }}
                    >
                      <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.3 }}
                      >
                        <Typography
                          sx={{
                            color: '#64748b',
                            fontSize: '0.95rem',
                            mb: 1,
                          }}
                        >
                          No workflows found.
                        </Typography>
                        <Typography
                          sx={{
                            color: '#94a3b8',
                            fontSize: '0.85rem',
                          }}
                        >
                          Create a new workflow to get started.
                        </Typography>
                      </motion.div>
                    </TableCell>
                  </TableRow>
                ) : (
                  <AnimatePresence>
                    {displayedRows.map((row, index) => (
                      <TableRow
                        key={row.workflow_id || index}
                        component={motion.tr}
                        custom={index}
                        variants={rowVariants}
                        initial="hidden"
                        animate="visible"
                        sx={{
                          borderBottom: '1px solid #F3F4F6',
                          backgroundColor:
                            index % 2 === 1 ? '#F3F5F7' : '#ffffff',
                          '&:hover': {
                            backgroundColor:
                              index % 2 === 1 ? '#71B9FF40' : '#71B9FF40',
                          },
                        }}
                      >
                        <TableCell
                          sx={{
                            // py: 0.25,
                            px: 2,
                            fontSize: '0.875rem',
                            fontWeight: 500,
                            color: '#6B7280',
                            verticalAlign: 'middle',
                            padding: 'none',
                          }}
                        >
                          {index + 1}
                        </TableCell>
                        <TableCell
                          sx={{
                            // py: 0.25,
                            padding: 'none',
                            px: 4,
                            fontSize: '0.875rem',
                            fontWeight: 500,
                            color: '#111827',
                            verticalAlign: 'middle',
                            cursor: 'pointer',
                            '&:hover': {
                              textDecoration: 'none',
                              color: 'inherit',
                            },
                          }}
                          onClick={(e) => handleEditClick(e, row)}
                        >
                          <Box
                            component={motion.span}
                            sx={{
                              color: '#4C5B9A',
                              textDecoration: 'none',
                              fontWeight: 500,
                              textTransform: 'capitalize',
                              letterSpacing: '0.02em',
                              display: 'inline-block',
                              transformOrigin: 'left center',
                              willChange: 'transform, color',
                              backfaceVisibility: 'hidden',
                              WebkitFontSmoothing: 'antialiased',
                              MozOsxFontSmoothing: 'grayscale',
                              textRendering: 'optimizeLegibility',
                              textShadow: '0 0 0 currentColor',
                            }}
                            style={{ transformPerspective: 1000 }}
                            whileHover={{ scaleX: 1.07, color: '#4C5B9A' }}
                            whileTap={{ scaleX: 1.04 }}
                            transition={{ duration: 0.18, ease: 'easeOut' }}
                          >
                            {row.name}
                          </Box>
                        </TableCell>
                        <TableCell
                          sx={{
                            // py: 0.25,
                            padding: 'none',
                            px: 2,
                            fontSize: '0.875rem',
                            color: '#6B7280',
                            fontWeight: 400,
                            verticalAlign: 'middle',
                          }}
                        >
                          {row.created_at
                            ? formatDate(row.created_at, { includeTime: true })
                            : '—'}
                        </TableCell>
                        <TableCell
                          sx={{
                            // py: 0.25,
                            padding: 'none',
                            px: 2,
                            fontSize: '0.875rem',
                            color: '#6b7280',
                            fontWeight: 400,
                            verticalAlign: 'middle',
                            maxWidth: 180,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {row.description || '—'}
                        </TableCell>
                        <TableCell
                          sx={{
                            padding: 'none',
                            px: 2,
                            fontSize: '0.875rem',
                            color: '#6b7280',
                            fontWeight: 400,
                            verticalAlign: 'middle',
                          }}
                        >
                          <Box
                            sx={{
                              display: 'inline-block',
                              px: 2,
                              py: 0.2,
                              borderRadius: '8px',
                              fontSize: '0.75rem',
                              fontWeight: 700,
                              textTransform: 'uppercase',
                              letterSpacing: '0.025em',
                              color: '#5965A6',
                            }}
                          >
                            {row.is_scheduled ? 'Schedule' : 'On Demand'}
                          </Box>
                        </TableCell>
                        {(isAdmin || isSuperAdmin) && (
                          <TableCell
                            sx={{
                              padding: 'none',
                              px: 2,
                              fontSize: '0.875rem',
                              color: '#6b7280',
                              fontWeight: 400,
                              verticalAlign: 'middle',
                            }}
                          >
                            {row.user_name || 'N/A'}
                          </TableCell>
                        )}
                        {isSuperAdmin && (
                          <TableCell
                            sx={{
                              padding: 'none',
                              px: 2,
                              fontSize: '0.875rem',
                              color: '#6b7280',
                              fontWeight: 400,
                              verticalAlign: 'middle',
                            }}
                          >
                            {row.org_name || 'N/A'}
                          </TableCell>
                        )}
                        <TableCell
                          sx={{
                            // py: 0.25,
                            padding: 'none',
                            px: 2,
                            verticalAlign: 'middle',
                          }}
                        >
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <Tooltip
                              title="Edit Workflow"
                              arrow
                              placement="top"
                            >
                              <motion.div whileTap={{ scale: 0.95 }}>
                                <IconButton
                                  size="small"
                                  // onClick={(e) => {
                                  //   e.stopPropagation();
                                  //   onEdit(row.workflow_id);
                                  // }}
                                  onClick={(e) => handleEditClick(e, row)}
                                  sx={{
                                    color: '#6b7280',
                                    padding: '6px',
                                    borderRadius: '6px',
                                  }}
                                >
                                  <img
                                    src={EditIcon}
                                    alt="Edit"
                                    style={{
                                      width: 22,
                                      height: 22,
                                    }}
                                  />
                                </IconButton>
                              </motion.div>
                            </Tooltip>
                            <Tooltip
                              title="Delete Workflow"
                              arrow
                              placement="top"
                            >
                              <motion.div whileTap={{ scale: 0.95 }}>
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onDelete(row.workflow_id);
                                  }}
                                  sx={{
                                    color: '#6b7280',
                                    padding: '6px',
                                    borderRadius: '6px',
                                  }}
                                >
                                  <img
                                    src={DeleteIcon}
                                    alt="Delete"
                                    style={{
                                      width: 22,
                                      height: 22,
                                    }}
                                  />
                                </IconButton>
                              </motion.div>
                            </Tooltip>
                            <Tooltip
                              title="Star Workflow"
                              arrow
                              placement="top"
                            >
                              <Box
                                component={motion.div}
                                whileTap={{ scale: 0.95 }}
                              >
                                <IconButton
                                  size="small"
                                  disabled={isStarUpdating(row.workflow_id)}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onToggleStar(
                                      row.workflow_id,
                                      !isRowStarred(row),
                                    ); // pass next boolean
                                  }}
                                  sx={{
                                    color: '#6b7280',
                                    p: '6px',
                                    borderRadius: '6px',
                                  }}
                                >
                                  {isStarUpdating(row.workflow_id) ? (
                                    // tiny inline spinner while PATCHing
                                    <Box
                                      sx={{
                                        width: 20,
                                        height: 20,
                                        border: '2px solid #cbd5e1',
                                        borderTopColor: '#5965A6',
                                        borderRadius: '50%',
                                        animation: 'spin 0.7s linear infinite',
                                        '@keyframes spin': {
                                          to: { transform: 'rotate(360deg)' },
                                        },
                                      }}
                                    />
                                  ) : (
                                    <img
                                      src={
                                        isRowStarred(row)
                                          ? StarFilled
                                          : StarOutline
                                      }
                                      alt={
                                        isRowStarred(row)
                                          ? 'Unstar workflow'
                                          : 'Star workflow'
                                      }
                                      style={{ width: 22, height: 22 }}
                                    />
                                  )}
                                </IconButton>
                              </Box>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </AnimatePresence>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </motion.div>
      </Box>
      <VerifyCaptchaDialog
        open={captchaOpen}
        onClose={handleClose}
        onVerified={handleVerified}
        title="Quick verification"
        description={`Please complete the check to edit "${
          pendingWorkflow?.name || 'this workflow'
        }".`}
      />
    </motion.div>
  );
};

WorkflowTable.propTypes = {
  title: PropTypes.string.isRequired,
  workflows: PropTypes.array.isRequired,
  loading: PropTypes.bool,
  onEdit: PropTypes.func,
  onDelete: PropTypes.func,
  showCreateButton: PropTypes.bool,
  selectedProject: PropTypes.object,
};

export default WorkflowTable;
